import {
  CloseOutlined,
  DownOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  MinusOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { Button, Dropdown, Flex, Modal, Space, Typography } from 'antd';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import styled from 'styled-components';
import { useDismissSuggestedThemes } from '../../../api/LLM';
import { useSuggestedGroups } from '../../../api/Questions';
import { useTagResponses } from '../../../api/Responses';
import {
  FilterSortType,
  QuestionProcessingType,
  TESelectionType,
  ThemeEnginePanel,
  ThemeSortOptions,
} from '../../../constants';
import useParams from '../../../hooks/useParams';
import { Gray5, Gray6, White } from '../../../styles';
import {
  displayKeyType,
  exploreThemesSelectionType,
  searchFilterType,
  suggestedThemeType,
  themeType,
} from '../../../types';
import AddToModal from '../components/AddToModal';
import BaseCollapse from '../components/BaseCollapse';
import CreateEditGroupModal from '../components/CreateEditGroupModal';
import FilterSortDropdown from '../components/FilterSortDropdown';
import MergeModal from '../components/MergeModal';
import SuggestedThemesDisplay from './SuggestedThemesDisplay';

const { Text } = Typography;
const { confirm } = Modal;

const TitleText = styled(Text)`
  font-weight: 600;
  font-size: 18px;
`;

const StyledButton = styled(Button)`
  background: ${Gray6};
  border: 0;
  box-shadow: unset;
  color: ${White} !important;

  &&.ant-btn:hover {
    background: ${Gray5};
    color: ${White};
  }
`;

const StyledCloseButton = styled(Button)`
  color: ${Gray5};
`;

function ExploreThemes({
  themes,
  suggestedThemes,
  phase,
  fullScreen = null,
  setFullScreen,
  isExpanded,
  showDescriptions,
  themeSpan,
  searchFilters,
  selectedSuggestions,
  handleSelection,
  collapseKey,
  setCollapseKey,
  displayKey,
  setDisplayKey,
  setScrollNeeded,
  createUpdateThemes,
  selectAllSuggestions,
  groupThemes,
  processingType,
}) {
  const groupNames = themes.filter((theme) => theme.is_parent).map((theme) => theme.name);
  const { questionId, codeFrameId } = useParams();

  const [activeSort, setActiveSort] = useState(ThemeSortOptions.SUGGESTED_GROUPS);
  const [addToModalVisible, setAddToModalVisible] = useState(false);
  const [mergeModalVisible, setMergeModalVisible] = useState(false);
  const [newGroupModalVisible, setNewGroupModalVisible] = useState(false);

  const { data: suggestedGroups, isLoading: suggestedGroupsFetching } = useSuggestedGroups(
    { questionId },
    {
      select: (data) =>
        data.data.filter((g) => suggestedThemes.filter((t) => t.group_id === g.id).length),
    },
  );

  const { mutate: dismissSuggestedThemes, isLoading: dismissSuggestedThemesLoading } =
    useDismissSuggestedThemes();
  const { mutate: tagResponses, mutateAsync: tagResponsesAsync } = useTagResponses();

  const addSuggestedThemes = () => {
    setDisplayKey({ type: null, key: null });
    const data = selectedSuggestions[TESelectionType.SUGGESTED_THEME].map((id) => ({
      name: suggestedThemes.find((t) => t.id === id).name,
      code_frame_id: codeFrameId,
      suggested_theme_id: id,
    }));
    createUpdateThemes.mutate(
      { codeFrameId, data },
      {
        onSuccess: async ({ data: themeData }) => {
          selectedSuggestions[TESelectionType.SUGGESTED_THEME].forEach((id, index) => {
            tagResponses({
              codeFrameId,
              data: {
                response_ids: suggestedThemes.find((t) => t.id === id).response_ids,
                theme_id: themeData.theme_ids[index],
                code_frame_id: codeFrameId,
              },
            });
          });
          handleSelection(null);
          setScrollNeeded(true);
        },
      },
    );
  };

  const addToGroup = (groupId) => {
    setDisplayKey({ type: null, key: null });
    const data = selectedSuggestions[TESelectionType.SUGGESTED_THEME].map((id) => ({
      name: suggestedThemes.find((t) => t.id === id).name,
      code_frame_id: codeFrameId,
      suggested_theme_id: id,
    }));
    createUpdateThemes.mutate(
      { codeFrameId, data },
      {
        onSuccess: ({ data: themeData }) => {
          groupThemes.mutate({
            codeFrameId,
            data: {
              group_id: groupId,
              theme_ids: themeData.theme_ids,
            },
          });
          selectedSuggestions[TESelectionType.SUGGESTED_THEME].forEach((id, index) => {
            tagResponses({
              codeFrameId,
              data: {
                response_ids: suggestedThemes.find((t) => t.id === id).response_ids,
                theme_id: themeData.theme_ids[index],
                code_frame_id: codeFrameId,
              },
            });
          });
          handleSelection(null);
        },
      },
    );
  };

  const mergeThemes = async ({ themeId, name, description }) => {
    setDisplayKey({ type: null, key: null });
    const data = [
      {
        name,
        description,
        code_frame_id: codeFrameId,
        suggested_theme_id: themeId,
      },
    ];
    const responseIds = [
      ...new Set(
        selectedSuggestions[TESelectionType.SUGGESTED_THEME].flatMap(
          (id) => suggestedThemes.find((t) => t.id === id).response_ids,
        ),
      ),
    ];
    createUpdateThemes.mutate(
      { codeFrameId, data },
      {
        onSuccess: async ({ data: themeData }) => {
          tagResponses({
            codeFrameId,
            data: {
              response_ids: responseIds,
              theme_id: themeData.theme_ids[0],
              code_frame_id: codeFrameId,
            },
          });
          dismissSuggestedThemes({
            codeFrameId,
            suggestedThemeIds: selectedSuggestions[TESelectionType.SUGGESTED_THEME].filter(
              (id) => id !== themeId,
            ),
          });
          handleSelection(null);
          setScrollNeeded(true);
        },
      },
    );
  };

  const newGroupWithSelection = ({ name }) => {
    setDisplayKey({ type: null, key: null });
    const newGroup = {
      name,
      code_frame_id: codeFrameId,
      is_group: true,
    };
    const newThemes = selectedSuggestions[TESelectionType.SUGGESTED_THEME].map((id) => ({
      name: suggestedThemes.find((t) => t.id === id).name,
      code_frame_id: codeFrameId,
      suggested_theme_id: id,
    }));
    const data = [newGroup, ...newThemes];
    createUpdateThemes.mutate(
      { codeFrameId, data },
      {
        onSuccess: ({ data: themeData }) => {
          groupThemes.mutate({
            codeFrameId,
            data: {
              group_id: themeData.theme_ids[0],
              theme_ids: themeData.theme_ids.slice(1),
            },
          });
          selectedSuggestions[TESelectionType.SUGGESTED_THEME].forEach((id, index) => {
            tagResponses({
              codeFrameId,
              data: {
                response_ids: suggestedThemes.find((t) => t.id === id).response_ids,
                theme_id: themeData.theme_ids[index + 1], // skip the first theme (group)
                code_frame_id: codeFrameId,
              },
            });
          });
          handleSelection(null);
        },
      },
    );
  };

  const addGroupsAndThemes = async () => {
    setDisplayKey({ type: null, key: null });

    // first add all themes that are not in a group
    const themesWithoutGroup = selectedSuggestions[TESelectionType.SUGGESTED_THEME].filter(
      (themeId) =>
        !selectedSuggestions[TESelectionType.SUGGESTED_GROUP].includes(
          suggestedThemes.find((t) => t.id === themeId).group_id,
        ),
    );
    const themesWithoutGroupData = themesWithoutGroup.map((id) => ({
      name: suggestedThemes.find((t) => t.id === id).name,
      code_frame_id: codeFrameId,
      suggested_theme_id: id,
    }));
    createUpdateThemes.mutate(
      { codeFrameId, data: themesWithoutGroupData },
      {
        onSuccess: async ({ data: themeData }) => {
          themesWithoutGroup.forEach((id, index) => {
            tagResponses({
              codeFrameId,
              data: {
                response_ids: suggestedThemes.find((t) => t.id === id).response_ids,
                theme_id: themeData.theme_ids[index],
                code_frame_id: codeFrameId,
              },
            });
          });
        },
      },
    );

    // then add all groups and themes that are in a group
    const groupedThemeIds = selectedSuggestions[TESelectionType.SUGGESTED_GROUP].map((groupId) => ({
      groupId,
      themeIds: selectedSuggestions[TESelectionType.SUGGESTED_THEME].filter(
        (tId) => suggestedThemes.find((t) => t.id === tId).group_id === groupId,
      ),
    }));
    groupedThemeIds.forEach(async ({ groupId, themeIds }) => {
      const newGroup = {
        name: suggestedGroups.find((g) => g.id === groupId).name,
        code_frame_id: codeFrameId,
        is_group: true,
      };
      const newThemes = themeIds.map((id) => ({
        name: suggestedThemes.find((t) => t.id === id).name,
        code_frame_id: codeFrameId,
        suggested_theme_id: id,
      }));
      const data = [newGroup, ...newThemes];
      const { data: themeData } = await createUpdateThemes.mutateAsync({ codeFrameId, data });
      const groupPromise = groupThemes.mutateAsync({
        codeFrameId,
        data: {
          group_id: themeData.theme_ids[0],
          theme_ids: themeData.theme_ids.slice(1),
        },
      });
      const tagPromises = themeIds.map((id, index) =>
        tagResponsesAsync({
          codeFrameId,
          data: {
            response_ids: suggestedThemes.find((t) => t.id === id).response_ids,
            theme_id: themeData.theme_ids[index + 1], // skip the first theme (group)
            code_frame_id: codeFrameId,
          },
        }),
      );
      await Promise.all([groupPromise, ...tagPromises]);
      handleSelection(null);
    });
  };

  const deleteSuggestedThemes = () => {
    setDisplayKey({ type: null, key: null });
    dismissSuggestedThemes({
      codeFrameId,
      suggestedThemeIds: selectedSuggestions[TESelectionType.SUGGESTED_THEME],
    });
    handleSelection(null);
  };

  const confirmDelete = () => {
    confirm({
      title: 'Are you sure you want to delete the selected AI suggested themes?',
      content: 'This cannot be undone.',
      okText: 'Delete',
      okButtonProps: { danger: true, type: 'primary' },
      onOk: deleteSuggestedThemes,
    });
  };

  const dismissAllSuggestions = () => {
    confirm({
      title: 'Dismiss all emerging themes?',
      content: 'This will remove all suggested themes from the drawer. This cannot be undone.',
      okText: 'Dismiss All',
      okButtonProps: { danger: true, type: 'primary' },
      onOk: () => {
        setDisplayKey({ type: null, key: null });
        dismissSuggestedThemes({
          codeFrameId,
          suggestedThemeIds: suggestedThemes.map((t) => t.id),
        });
        handleSelection(null);
      },
    });
  };

  const onCollapseChange = () => {
    if (collapseKey.length > 1) {
      setCollapseKey([ThemeEnginePanel.EXPLORE_THEMES]);
    } else if (collapseKey.includes(ThemeEnginePanel.EXPLORE_THEMES)) {
      setCollapseKey([ThemeEnginePanel.EXPLORE_THEMES, ThemeEnginePanel.MY_CODE_FRAME]);
    } else if (collapseKey.includes(ThemeEnginePanel.MY_CODE_FRAME)) {
      setCollapseKey([ThemeEnginePanel.EXPLORE_THEMES]);
    }
  };

  const actionButtons = selectedSuggestions[TESelectionType.SUGGESTED_GROUP].length
    ? [{ text: 'Add', onClick: addGroupsAndThemes, type: 'primary' }]
    : [
        {
          text: 'Delete',
          onClick: confirmDelete,
          color: 'danger',
          variant: 'outlined',
          danger: true,
        },
        ...(selectedSuggestions[TESelectionType.SUGGESTED_THEME].length > 1
          ? [{ text: 'Merge', onClick: () => setMergeModalVisible(true) }]
          : []),
        { text: 'Add as new group', onClick: () => setNewGroupModalVisible(true) },
        { text: 'Add to...', onClick: () => setAddToModalVisible(true) },
        { text: 'Add', onClick: addSuggestedThemes, type: 'primary' },
      ];

  const item = {
    key: ThemeEnginePanel.EXPLORE_THEMES,
    label: (
      <TitleText>
        {processingType === QuestionProcessingType.REUSE_CODE_FRAME
          ? 'Emerging themes'
          : 'AI generated themes'}
      </TitleText>
    ),
    content: (
      <SuggestedThemesDisplay
        phase={phase}
        activeSort={activeSort}
        selectedSuggestions={selectedSuggestions}
        handleSelection={handleSelection}
        showDescriptions={showDescriptions}
        suggestedGroups={suggestedGroups}
        suggestedThemes={suggestedThemes}
        themeSpan={themeSpan}
        searchFilters={searchFilters}
        displayKey={displayKey}
        setDisplayKey={setDisplayKey}
        processingType={processingType}
      />
    ),
    extra: (
      <Space size={14}>
        <FilterSortDropdown
          type={FilterSortType.SORT}
          options={ThemeSortOptions}
          activeOption={activeSort}
          onChange={(sort) => {
            setActiveSort(sort);
            setDisplayKey({ type: null, key: null });
            handleSelection(null);
          }}
          isExpanded={!!fullScreen || isExpanded}
        />
        {selectedSuggestions[TESelectionType.SUGGESTED_GROUP].length ===
          (suggestedGroups?.length ?? 0) &&
        selectedSuggestions[TESelectionType.SUGGESTED_THEME].length === suggestedThemes.length ? (
          <Button onClick={() => handleSelection(null)}>Deselect all</Button>
        ) : (
          <Button onClick={selectAllSuggestions}>Select all</Button>
        )}
        {processingType === QuestionProcessingType.REUSE_CODE_FRAME &&
          suggestedThemes.length > 0 && (
            <Button danger onClick={dismissAllSuggestions}>
              Dismiss All
            </Button>
          )}
        <StyledButton
          icon={fullScreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
          onClick={() => setFullScreen(fullScreen ? null : ThemeEnginePanel.EXPLORE_THEMES)}
        />
      </Space>
    ),
    footer: selectedSuggestions[TESelectionType.SUGGESTED_THEME].length > 0 && (
      <Flex justify="space-between" align="center">
        <Flex gap={4}>
          <StyledCloseButton
            type="text"
            size="small"
            shape="circle"
            icon={<CloseOutlined />}
            onClick={() => {
              handleSelection(null);
            }}
          />
          <Text type="secondary">
            {selectedSuggestions[TESelectionType.SUGGESTED_GROUP].length > 0
              ? `${selectedSuggestions[TESelectionType.SUGGESTED_GROUP].length} groups selected`
              : `${selectedSuggestions[TESelectionType.SUGGESTED_THEME].length} selected`}
          </Text>
        </Flex>
        {isExpanded ? (
          <Space>
            {actionButtons.map(({ text, onClick, ...props }) => (
              // eslint-disable-next-line react/jsx-props-no-spreading
              <Button key={text} onClick={onClick} {...props}>
                {text}
              </Button>
            ))}
          </Space>
        ) : (
          <Dropdown
            menu={{
              items: actionButtons.map(({ text, onClick, ...props }) => ({
                label: text,
                key: text,
                onClick,
                ...props,
              })),
            }}
          >
            <Button>
              <Space>
                Actions
                <DownOutlined />
              </Space>
            </Button>
          </Dropdown>
        )}
      </Flex>
    ),
  };

  const expandIcon = () =>
    fullScreen ? null : (
      <StyledButton
        icon={
          collapseKey.length > 1 || !collapseKey.includes(ThemeEnginePanel.EXPLORE_THEMES) ? (
            <PlusOutlined />
          ) : (
            <MinusOutlined />
          )
        }
      />
    );

  return (
    <>
      <BaseCollapse
        collapsible="icon"
        expandIcon={expandIcon}
        expandIconPosition="end"
        bordered={false}
        activeKey={fullScreen ? [fullScreen] : collapseKey}
        onChange={onCollapseChange}
        item={item}
        loading={suggestedGroupsFetching || dismissSuggestedThemesLoading}
        collapsed={!fullScreen && !collapseKey.includes(ThemeEnginePanel.EXPLORE_THEMES)}
        visible={fullScreen !== ThemeEnginePanel.MY_CODE_FRAME}
      />
      <AddToModal
        panel={ThemeEnginePanel.EXPLORE_THEMES}
        visible={addToModalVisible}
        setVisible={setAddToModalVisible}
        onAdd={addToGroup}
        groups={themes.filter((t) => t.is_parent)}
        selectedThemes={suggestedThemes.filter((t) =>
          selectedSuggestions[TESelectionType.SUGGESTED_THEME].includes(t.id),
        )}
        handleSelection={handleSelection}
      />
      <MergeModal
        panel={ThemeEnginePanel.EXPLORE_THEMES}
        visible={mergeModalVisible}
        setVisible={setMergeModalVisible}
        onMerge={mergeThemes}
        themes={suggestedThemes.filter((t) =>
          selectedSuggestions[TESelectionType.SUGGESTED_THEME].includes(t.id),
        )}
      />
      <CreateEditGroupModal
        panel={ThemeEnginePanel.EXPLORE_THEMES}
        visible={newGroupModalVisible}
        setVisible={setNewGroupModalVisible}
        onOk={newGroupWithSelection}
        selectedThemes={suggestedThemes.filter((t) =>
          selectedSuggestions[TESelectionType.SUGGESTED_THEME].includes(t.id),
        )}
        handleSelection={handleSelection}
        existingNames={groupNames}
      />
    </>
  );
}

ExploreThemes.propTypes = {
  themes: PropTypes.arrayOf(themeType).isRequired,
  suggestedThemes: PropTypes.arrayOf(suggestedThemeType).isRequired,
  phase: PropTypes.string.isRequired,
  fullScreen: PropTypes.string,
  setFullScreen: PropTypes.func.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  showDescriptions: PropTypes.bool.isRequired,
  themeSpan: PropTypes.number.isRequired,
  searchFilters: searchFilterType.isRequired,
  selectedSuggestions: exploreThemesSelectionType.isRequired,
  handleSelection: PropTypes.func.isRequired,
  collapseKey: PropTypes.arrayOf(PropTypes.string).isRequired,
  setCollapseKey: PropTypes.func.isRequired,
  displayKey: displayKeyType.isRequired,
  setDisplayKey: PropTypes.func.isRequired,
  setScrollNeeded: PropTypes.func.isRequired,
  createUpdateThemes: PropTypes.shape({
    mutate: PropTypes.func,
    mutateAsync: PropTypes.func,
  }).isRequired,
  selectAllSuggestions: PropTypes.func.isRequired,
  groupThemes: PropTypes.shape({
    mutate: PropTypes.func,
    mutateAsync: PropTypes.func,
  }).isRequired,
  processingType: PropTypes.string.isRequired,
};

export default ExploreThemes;
